// Reading a purchase's discount, whatever gave it: a discount code, a loyalty
// reward or a referral reward. The server flattens all three into the same
// four fields (util/purchase-discount.ts there); every surface that shows a
// single sale reads them through here, so a new source only needs one change.
import { translate, translateWithVars } from "shared/languages";
import { DiscountSource } from "shared/types/enum";
import { formatCurrency } from "shared/utils";

export type PurchaseDiscountFields = {
  discount_source?: DiscountSource | null;
  discount_code?: string | null;
  discount_amount?: number | null;
  original_price?: number | null;
  discount_percent?: number | null;
};

// Null when no discount applied. `discount_source` is absent from a server
// deployed before loyalty/referral discounts were read; that server only ever
// sent code discounts, so a code alone still means a code.
export const discountSourceOf = (
  p: PurchaseDiscountFields
): DiscountSource | null => {
  if (!p.discount_amount) return null;
  return p.discount_source ?? (p.discount_code ? DiscountSource.Code : null);
};

// The short name of where a discount came from: the code itself for a code,
// otherwise Loyalty or Referral. Null when there was no discount.
export const discountSourceLabel = (
  p: PurchaseDiscountFields
): string | null => {
  switch (discountSourceOf(p)) {
    case DiscountSource.Code:
      return p.discount_code || translate("purchaseDiscount.filter_code");
    case DiscountSource.Loyalty:
      return translate("purchaseDiscount.source_loyalty");
    case DiscountSource.Referral:
      return translate("purchaseDiscount.source_referral");
    default:
      return null;
  }
};

// "Diskon −Rp 300.000 · Loyalty", the one-line note shown under a price paid
// (client detail's Transaksi tab, the package card). Null when no discount.
export const discountSummary = (p: PurchaseDiscountFields): string | null => {
  const source = discountSourceLabel(p);
  if (!source || !p.discount_amount) return null;
  return translateWithVars("purchaseDiscount.summary", {
    amount: `−${formatCurrency(p.discount_amount)}`,
    source,
  });
};

// The Invoice/Receipt row naming the discount: "Diskon (HEMAT10, 15%)",
// "Diskon Loyalty (20%)" or "Diskon Referral (30%)". A percentage discount
// carries its rate in the brackets, beside the code for a code (System Review
// N11); a fixed-amount reward or code has none, since its rupiah figure is
// already the row's amount. Called per render from the lazy PDF chunk, never
// at module scope.
export const discountInvoiceLabel = (
  source: DiscountSource | null,
  code: string | null,
  percent: number | null = null
): string => {
  const withPercent = (label: string) =>
    percent ? `${label} (${percent}%)` : label;
  switch (source) {
    case DiscountSource.Loyalty:
      return withPercent(translate("purchaseDiscount.invoice_loyalty"));
    case DiscountSource.Referral:
      return withPercent(translate("purchaseDiscount.invoice_referral"));
    default:
      if (!code) return translate("purchaseDiscount.column");
      return percent
        ? translateWithVars("purchaseDiscount.invoice_code_percent", { code, percent })
        : translateWithVars("purchaseDiscount.invoice_code", { code });
  }
};
